/**
 * usePopupTableColumns - Table columns hook for popup windows
 *
 * Reuses the same column creation logic from useCreateColumns.
 * Uses normal density (popups don't have settings context).
 */
import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import { sortTableHeaders } from '../Reports/Table';
import { filterTableHeaders } from '../Reports/Table';
import { createColumns } from '../Reports/Table/hooks';
import { useDateTimeFormat } from '../conversions/useDateFormat';

export interface UsePopupTableColumnsProps {
  shipData: any[] | undefined;
}

// Fixed density classes for popup tables (compact by default)
const densityClasses = {
  text: 'text-xs',
  tableCell: 'py-1 px-2',
  tableHeader: 'py-1 px-2',
  cellContent: 'text-xs',
  cardHeader: 'p-2',
  icon: { small: 'h-3 w-3', medium: 'h-4 w-4', large: 'h-5 w-5' },
  button: 'h-6 w-6',
  truncation: { short: 'max-w-16', medium: 'max-w-24', long: 'max-w-32' },
};

export const usePopupTableColumns = ({
  shipData,
}: UsePopupTableColumnsProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const formatDateTime = (date: Date | null | undefined): string => {
    if (!date) return '-';
    return formatDateTimeWithTimeZone(date);
  };

  const processedHeaders = useMemo(() => {
    if (!shipData || shipData.length === 0) return [];

    const rawHeaders = Object.keys(shipData[0]);
    const filteredHeaders = filterTableHeaders(rawHeaders);
    return sortTableHeaders(filteredHeaders);
  }, [shipData]);

  const columns = useMemo((): ColumnDef<any>[] => {
    if (processedHeaders.length === 0) return [];

    return createColumns(processedHeaders, formatDateTime, {
      tableDensity: 'normal',
      densityClasses,
    });
  }, [processedHeaders, formatDateTime]);

  return {
    columns,
    processedHeaders,
  };
};
